import React, { useState, useEffect } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Payment } from '@mui/icons-material';
import { paymentsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';

/**
 * Payment Button Component
 * Handles Razorpay payment flow
 */
const PaymentButton = ({ onSuccess, onError, disabled, children, ...buttonProps }) => {
  const { showError, showSuccess, showInfo } = useNotification();
  const [loading, setLoading] = useState(false);
  const [razorpayConfig, setRazorpayConfig] = useState(null);

  useEffect(() => {
    loadPaymentConfig();
  }, []);

  const loadPaymentConfig = async () => {
    try {
      const response = await paymentsApi.getConfig();
      setRazorpayConfig(response.data);

      // Load Razorpay script
      if (response.data.enabled && !window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
      }
    } catch (error) {
      console.error('Error loading payment config:', error);
      // Set default config on error
      setRazorpayConfig({ pagesPerMonth: 100 });
    }
  };

  const handlePayment = async () => {
    if (!razorpayConfig || !razorpayConfig.enabled) {
      showError('Payment system is not configured');
      return;
    }

    if (!window.Razorpay) {
      showError('Payment system is loading. Please try again.');
      return;
    }

    try {
      setLoading(true);

      // Create order
      const orderResponse = await paymentsApi.createOrder();
      const { orderId, amount, currency, razorpayKeyId } = orderResponse.data;

      // Configure Razorpay options
      const options = {
        key: razorpayKeyId,
        amount: amount,
        currency: currency,
        name: 'PinePocket',
        description: `Monthly Subscription - ${razorpayConfig.pagesPerMonth || 100} Pages`,
        order_id: orderId,
        handler: async function (response) {
          try {
            // Verify payment on backend
            const verifyResponse = await paymentsApi.verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });

            showSuccess('Payment successful! Your subscription is now active.');

            if (onSuccess) {
              onSuccess(verifyResponse.data);
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            showError('Payment verification failed. Please contact support.');

            if (onError) {
              onError(error);
            }
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: '',
        },
        notes: {
          description: 'Monthly Subscription',
        },
        theme: {
          color: '#1976d2', // Primary color
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            showInfo('Payment cancelled');
          },
        },
      };

      // Open Razorpay checkout
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        console.error('Payment failed:', response.error);
        showError(`Payment failed: ${response.error.description}`);
        setLoading(false);

        if (onError) {
          onError(response.error);
        }
      });

      rzp.open();
    } catch (error) {
      console.error('Error initiating payment:', error);
      showError(error.response?.data?.message || 'Failed to initiate payment');
      setLoading(false);

      if (onError) {
        onError(error);
      }
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      size="large"
      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Payment />}
      onClick={handlePayment}
      disabled={disabled || loading || !razorpayConfig?.enabled}
      {...buttonProps}
    >
      {loading ? 'Processing...' : children || 'Subscribe Now'}
    </Button>
  );
};

export default PaymentButton;

